import React, { useState, useEffect, useCallback } from 'react';
import { SignUpReportRowDto } from '@repo/common-types';
import StandardLayout from '@/common/layouts/StandardLayout'; // Corrected import path
import { Container, Row, Col, Card, CardBody, Button, Input, Label, Spinner } from 'reactstrap'; // Import reactstrap components
import SignUpReportTable from './components/SignUpReportTable'; // Import the new table component
import { api } from '@/services/api'; // Import the configured Axios instance
import { downloadBlobAsFile } from '@/common/utils/download-file';

const SignUpReportPage: React.FC = () => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [reportData, setReportData] = useState<SignUpReportRowDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingCsv, setDownloadingCsv] = useState<boolean>(false); // Add state for download loading
  // Pagination state (if needed later)
  // const [page, setPage] = useState<number>(1);
  // const [limit, setLimit] = useState<number>(10);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Create query params object for Axios
    const params: { startDate?: string; endDate?: string; page?: number; limit?: number } = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    // if (page) params.page = page;
    // if (limit) params.limit = limit;

    try {
      // Use the imported api instance (Axios) instead of fetch
      const response = await api.get<SignUpReportRowDto[]>('/v1/admin/user/reports/signup', { params });
      // Axios puts response data directly in `data` property
      setReportData(response.data);
    } catch (e: any) {
      // Catch Axios errors
      console.error('Failed to fetch report data:', e);
      const message = e.response?.data?.message || e.message || 'An unknown error occurred';
      setError(message);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate /*, page, limit */]);

  useEffect(() => {
    fetchReportData(); // Initial fetch
  }, [fetchReportData]);

  const handleDownloadCsv = async () => {
    // Make async
    setDownloadingCsv(true); // Indicate download start
    setError(null); // Clear previous errors

    const params: { startDate?: string; endDate?: string } = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    try {
      const response = await api.get('/v1/admin/user/reports/signup/csv', {
        params: params,
        responseType: 'blob', // Important: Ask Axios to expect a Blob
      });

      downloadBlobAsFile({
        blob: new Blob([response.data], { type: 'text/csv;charset=utf-8;' }),
        filename: 'signup_report.csv',
      });
    } catch (e: any) {
      console.error('Failed to download CSV:', e);
      const message = e.response?.data?.message || e.message || 'Failed to download CSV';
      // Try to read error message from blob if available
      if (e.response?.data instanceof Blob) {
        try {
          const errorText = await e.response.data.text();
          const errorJson = JSON.parse(errorText);
          setError(errorJson.message || message);
        } catch (parseError) {
          setError(message);
        }
      } else {
        setError(message);
      }
    } finally {
      setDownloadingCsv(false); // Indicate download end
    }
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  return (
    <StandardLayout pageTitle="Sign Up Report">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                {/* Filters Row */}
                <Row className="align-items-end mb-3">
                  <Col md={3}>
                    <Label htmlFor="startDate">Start Date:</Label>
                    <Input type="date" id="startDate" value={startDate} onChange={handleStartDateChange} />
                  </Col>
                  <Col md={3}>
                    <Label htmlFor="endDate">End Date:</Label>
                    <Input type="date" id="endDate" value={endDate} onChange={handleEndDateChange} />
                  </Col>
                  <Col md="auto">
                    <Button color="primary" onClick={fetchReportData} disabled={loading}>
                      {loading ? <Spinner size="sm" className="me-1" /> : null}
                      Fetch Report
                    </Button>
                  </Col>
                  <Col md="auto">
                    <Button
                      color="secondary"
                      onClick={handleDownloadCsv}
                      disabled={loading || downloadingCsv || reportData.length === 0}
                    >
                      {downloadingCsv ? <Spinner size="sm" className="me-1" /> : null}
                      Download CSV
                    </Button>
                  </Col>
                </Row>

                {/* Table Row */}
                <Row>
                  <Col lg={12}>
                    <SignUpReportTable data={reportData} loading={loading} error={error} />
                  </Col>
                </Row>
                {/* TODO: Add Pagination controls if needed */}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </StandardLayout>
  );
};

export default SignUpReportPage;
